import {useCallback} from 'react';

import CryptoJS from 'crypto-js';

import {IPutSplitBillsImage} from '@/app/main/split-bills/interfaces';
import {selectUser, useAppSelector} from '@/app/store';
import {
  IMultiPurposeChatRequest,
  IMultiPurposeChatResponse,
  IMultiPurposeChatSessionResponse,
  PostFreshchatRestoreIdResponse,
} from '@/dataSource/src/api/split-bills/entities';
import logError from '@/utils/errors/logError';

import {
  useLazyGetMultiPurposeChatSessionQuery,
  useLazyGetPostBillImageResultQuery,
  usePostBillImageMutation,
  usePostFreshchatRestoreIdMutation,
  useSendMultiPurposeChatMutation,
  useSplitBillsMutation,
} from './api';
import {uploadFileToS3SplitBills} from './s3';

async function calculateMD5(file: File): Promise<string> {
  const arrayBuffer = await readFileAsArrayBuffer(file);
  const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
  return CryptoJS.MD5(wordArray).toString(CryptoJS.enc.Base64);
}

function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => resolve(event.target?.result as ArrayBuffer);
    reader.onerror = error => reject(error);
    reader.readAsArrayBuffer(file);
  });
}

export const useSplitBillsRepository = () => {
  const [triggerSplitBills, {isLoading: isLoadingMessage}] =
    useSplitBillsMutation();
  const [triggerPostBillSubmitted] = usePostBillImageMutation();
  const [triggerGetBillImageResult] = useLazyGetPostBillImageResultQuery();
  const [triggerSendMultiPurposeChat] = useSendMultiPurposeChatMutation();
  const [triggerGetSessionMessages] = useLazyGetMultiPurposeChatSessionQuery();
  const [triggerGetFreshChatRestoreId] = usePostFreshchatRestoreIdMutation();

  const userData = useAppSelector(selectUser);

  const sendNewMessage = useCallback(
    async ({session_id, user_prompt, role}) => {
      try {
        const result = await triggerSplitBills({
          user_prompt,
          ...(session_id && {session_id}),
          role,
        }).unwrap();
        return result;
      } catch (error) {
        logError(error, {
          customTitle: 'Mobot - Error sending new message',
          fileName: 'split-bills - useRepository.ts',
          functionName: 'sendNewMessage',
          extraTags: {
            ...(session_id && {session_id}),
          },
        });
        throw error;
      }
    },
    [triggerSplitBills],
  );

  const handlePostBillSubmitted = useCallback(
    async (document: IPutSplitBillsImage, sessionId?: string) => {
      try {
        const hash = await calculateMD5(document.file);

        const documentMetadata: any = {
          content_type: document.file.type,
          content_md5: hash,
          ...(sessionId && {session_id: sessionId}),
        };
        const result =
          await triggerPostBillSubmitted(documentMetadata).unwrap();

        const bodyRequestS3: any = {
          document: documentMetadata,
          file: document.file,
          userId: userData?.id,
        };
        await uploadFileToS3SplitBills(
          bodyRequestS3,
          result.uploadUrl,
          result.sessionId,
        );
        return result.sessionId;
      } catch (error) {
        logError(error, {
          customTitle: 'Mobot - Error submitting bill',
          fileName: 'split-bills - useRepository.ts',
          functionName: 'handlePostBillSubmitted',
        });
      }
    },
    [triggerPostBillSubmitted, userData?.id],
  );

  const getUploadedBillResult = useCallback(
    async (sessionId: string) => {
      try {
        const result = await triggerGetBillImageResult(sessionId).unwrap();

        return result;
      } catch (error) {
        logError(error, {
          customTitle: 'Mobot - Error fetching uploaded bill result',
          fileName: 'split-bills - useRepository.ts',
          functionName: 'getUploadedBillResult',
          extraTags: {session_id: sessionId},
        });
      }
    },
    [triggerGetBillImageResult],
  );

  const handleSendMessage = useCallback(
    async (
      request: IMultiPurposeChatRequest,
    ): Promise<IMultiPurposeChatResponse> => {
      try {
        const result = await triggerSendMultiPurposeChat(request).unwrap();
        return result;
      } catch (error) {
        logError(error, {
          customTitle: 'Mobot - Error sending multi-purpose chat message',
          fileName: 'split-bills - useRepository.ts',
          functionName: 'handleSendMessage',
          extraTags: {
            ...(request?.session_id && {session_id: request.session_id}),
          },
          data: {request},
        });
        throw error;
      }
    },
    [triggerSendMultiPurposeChat],
  );
  const handleFreshchatRestoreId = useCallback(
    async (session_id: string): Promise<PostFreshchatRestoreIdResponse> => {
      try {
        const result = await triggerGetFreshChatRestoreId({
          session_id,
        }).unwrap();
        return result;
      } catch (error) {
        logError(error, {
          customTitle: 'Mobot - Error retrieving Freshchat restore ID',
          fileName: 'split-bills - useRepository.ts',
          functionName: 'handleFreshchatRestoreId',
          extraTags: {
            ...(session_id && {session_id: session_id}),
          },
          data: {session_id},
        });
        throw error;
      }
    },
    [triggerGetFreshChatRestoreId],
  );

  const handleGetMessages = useCallback(
    async (sessionId: string): Promise<IMultiPurposeChatSessionResponse> => {
      try {
        const result = await triggerGetSessionMessages(sessionId).unwrap();
        return result;
      } catch (error) {
        logError(error, {
          customTitle: 'Mobot - Error fetching multi-purpose chat messages',
          fileName: 'split-bills - useRepository.ts',
          functionName: 'handleGetMessages',
          extraTags: {session_id: sessionId},
        });
        throw error;
      }
    },
    [triggerGetSessionMessages],
  );

  return {
    sendNewMessage,
    isLoadingMessage,
    handlePostBillSubmitted,
    getUploadedBillResult,
    handleSendMessage,
    handleGetMessages,
    handleFreshchatRestoreId,
  };
};
